import React, { useState, useEffect } from "react";
import { Camera, Save, ArrowLeft } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { updateUser, profileInfo } from "../../../features/userSlice";
import avatarPlaceholder from "../../../assets/img/avater.png";
import { Link } from "react-router-dom";
import API from "../../../utils/Api";

const EditProfilePage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);

  const [formData, setFormData] = useState({
    name: "",
    username: "",
    bio: "",
    job: "",
    location: "",
    website: "",
    avatarUrl: "",
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState("");
  const [toast, setToast] = useState("");

  useEffect(() => {
    dispatch(profileInfo());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        username: user.username || "",
        bio: user.bio || "",
        job: user.job || "",
        location: user.location || "",
        website: user.website || "",
        avatarUrl: user.avatarUrl || "",
      });
    }
  }, [user]);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (avatarPreview) {
      URL.revokeObjectURL(avatarPreview);
    }

    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    try {
      await dispatch(
        updateUser({
          ...formData,
          avatarFile,
        })
      ).unwrap();

      setToast("Profile updated successfully");
      setTimeout(() => setToast(""), 2500);
    } catch (error) {
      setToast("Failed to update profile");
      setTimeout(() => setToast(""), 2500);
    }
  };

  const apiBase = API.defaults.baseURL?.replace(/\/api\/?$/, "") || "";
  const resolvedAvatarUrl = formData.avatarUrl
    ? formData.avatarUrl.startsWith("http")
      ? formData.avatarUrl
      : `${apiBase}${formData.avatarUrl}`
    : "";
  const avatarSrc = avatarPreview || resolvedAvatarUrl || avatarPlaceholder;

  return (
    <div className="bg-gray-50 flex justify-center px-4 py-6">
      <div className="w-full max-w-3xl bg-white rounded-xl shadow-md p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <Link
            to="/profile"
            className="flex items-center text-sm text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft size={18} className="mr-1" /> Back
          </Link>
          <h2 className="text-lg font-semibold">Edit Profile</h2>
        </div>

        {/* Avatar */}
        <div className="flex items-center gap-4 mb-6">
          <div className="relative">
            <img
              src={avatarSrc}
              alt="avatar"
              className="w-20 h-20 rounded-full object-cover border"
            />
            <label className="absolute -bottom-1 -right-1 bg-indigo-600 p-1.5 rounded-full text-white cursor-pointer">
              <Camera size={14} />
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarChange}
              />
            </label>
          </div>

          <div>
            <p className="font-medium">{formData.name || "Your Name"}</p>
            <p className="text-xs text-gray-500">
              JPG, PNG • Max 2MB
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Full name"
          />

          <Input
            label="Username"
            name="username"
            value={formData.username}
            onChange={handleChange}
            placeholder="Username"
          />

          <Input
            label="Job"
            name="job"
            value={formData.job}
            onChange={handleChange}
            placeholder="Job title"
          />

          <Input
            label="Location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            placeholder="City, Country"
          />

          <Input
            label="Website"
            name="website"
            value={formData.website}
            onChange={handleChange}
            placeholder="https://example.com"
          />

          <div className="md:col-span-2">
            <label className="text-sm font-medium text-gray-700">
              Bio
            </label>
            <textarea
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              rows="3"
              placeholder="Short bio..."
              className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
            />
          </div>
        </div>

        {/* Save */}
        <div className="mt-5 flex justify-end">
          <button
            onClick={handleSave}
            className="flex items-center gap-2 bg-indigo-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-indigo-700"
          >
            <Save size={16} /> Save Changes
          </button>
        </div>
      </div>

      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
};

const Input = ({ label, ...props }) => (
  <div>
    <label className="text-sm font-medium text-gray-700">
      {label}
    </label>
    <input
      {...props}
      className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
    />
  </div>
);

export default EditProfilePage;
